/**
 * Formatting helpers for the admin guest list.
 * Pure functions so they can be shared by the UI and the CSV export.
 */

import type { Timestamp } from 'firebase/firestore';

export type ResponseValue = string | number | boolean | string[];

/** "favorite_color" -> "Favorite Color" */
export const formatResponseKey = (key: string) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

export const formatResponseValue = (value: ResponseValue): string => {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

/** Local-time "YYYY-MM-DD" key used to group guests by day. */
export const getLocalDateKey = (date: Date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export type DayHeader = {
  /** "Today", "Yesterday", or "Sunday, October 5" */
  primary: string;
  /** The full date when `primary` is a relative word, otherwise undefined. */
  secondary?: string;
};

const formatLongDate = (date: Date, withYear: boolean) =>
  new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' as const } : {}),
  }).format(date);

export const formatDayHeader = (
  date: Date,
  now: Date = new Date()
): DayHeader => {
  const key = getLocalDateKey(date);
  const withYear = date.getFullYear() !== now.getFullYear();
  const full = formatLongDate(date, withYear);

  if (key === getLocalDateKey(now)) {
    return { primary: 'Today', secondary: full };
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (key === getLocalDateKey(yesterday)) {
    return { primary: 'Yesterday', secondary: full };
  }

  return { primary: full };
};

/** "Oct 5, 3:42 PM" */
export const formatShortDateTime = (date: Date) =>
  new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);

/** "Sunday, October 5, 2026 at 3:42 PM" */
export const formatLongDateTime = (date: Date) =>
  new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);

/** Safely unwrap a Firestore Timestamp (or a partially hydrated one). */
export const toDate = (value?: Timestamp | null): Date | null =>
  value?.toDate?.() ?? null;

/** The 4th session photo is the generated strip, stored as strip.jpg. */
export const isPhotoStripUrl = (url: string) => {
  try {
    return /\/strip\.jpg/i.test(decodeURIComponent(url));
  } catch {
    return false;
  }
};

export const getEmailInitial = (email: string) =>
  (email.trim()[0] ?? '?').toUpperCase();

/** Stable 0..3 index so each guest keeps the same avatar tint. */
export const getAvatarHue = (seed: string) => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return hash % 4;
};
